// OnboardingStep4.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import {
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";
import { StandardButton } from "@/components/common/StandardButton";
import CustomCheckbox from "@/components/common/CustomCheckbox";
import { ArrowLeft } from "lucide-react";

interface OnboardingStep4Props {
  title: string;
  subtitle: string;
}

const OnboardingStep4 = ({ title, subtitle }: OnboardingStep4Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const { modalStyle, modalClassName } = useModalHeight();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();

  //fetch data options to display from the dedicated accessibility endpoint
  const { filterAccessibility } = useAccessibility();
  const tagsToAdd: any[] = [];
  const prepareData = () => {
    const ids = [964, 844, 553, 554, 753];
    const idstoAdd: any[] = [];
    filterAccessibility.forEach((option: any) => {
      option.accessibility_tags.forEach((o: any) => {
        if (ids.includes(o.id)) {
          tagsToAdd.push(o);
          idstoAdd.push(o.id);
        }
      });
    });
    return filterAccessibility.map((option: any) => {
      const filteredTags = option.accessibility_tags.filter(
        (o: any) => !idstoAdd.includes(o.id),
      );
      return { ...option, accessibility_tags: filteredTags };
    });
  };
  const preparedData = prepareData();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountAccessibilityFeatureYouRequirePage",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountAccessibilityFeatureYouRequirePage",
      {},
    );
    const selectedString = submitData.accessibilityFeatureRequestTagsValue;
    if (selectedString) {
      const selected = selectedString.split(",").map((s) => parseInt(s));
      setSelectedOptions(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionChange = (option: number) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onBack = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep4BackButtonClicked",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep4BackButtonClicked",
      {},
    );
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const onSkip = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep4Skip");
    AnalyticsService.trackMx("authPopupCreateAccountStep4Skip", {});
    setScreenIndex(AuthModalScreen.OnboardingStep5);
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep4Completed",
    );
    AnalyticsService.trackMx("authPopupCreateAccountStep4Completed", {});

    setSubmitData((prev) => ({
      ...prev,
      accessibilityFeatureRequestTagsValue: selectedOptions.join(","),
    }));

    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep5);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onBack}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="flex-1 px-8 md:px-16 overflow-y-scroll">
        <div className="mt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
          <div>
            <hr className="w-full border-border-token-brand-minimal my-5" />
          </div>
        </div>
        <div className="mb-6">
          <p className="title-s-semibold text-fg-neutral-strong mb-3">
            {i18n.get("whatAccessibilityFeaturesDoYouRequire")}
          </p>
          <p className="body-l-semibold !text-fg-brand-subtle block">
            {i18n.get("selectAllThatApply2")}
          </p>
        </div>
        <div className="flex flex-col gap-2.5">
          {preparedData.map((option, index) => {
            if (option.accessibility_tags.length <= 0) return null;
            return (
              <div key={index} className="">
                <h3 className="title-s-semibold text-fg-neutral-strong mb-4">
                  {option.name}
                </h3>
                {/* Adjusted for semantic HTML */}
                {option.accessibility_tags.map((o) => (
                  <div key={o.id} className="my-2" role="none">
                    <CustomCheckbox
                      label={o.name}
                      checked={selectedOptions.includes(o.id)}
                      onChange={() => handleOptionChange(o.id)}
                    />
                    {o.id === 428 && selectedOptions.includes(o.id) && (
                      <div key={o.id + "321"} className="ml-8">
                        <h3 className="title-s-semibold text-fg-neutral-strong mt-2">
                          {i18n.get("wouldAnyOfTheseAlsoWork")}
                        </h3>
                        {/* Added text content */}
                        {tagsToAdd.map((tag) => (
                          <div key={tag.id} className="mt-2">
                            <CustomCheckbox
                              label={tag.name}
                              checked={selectedOptions.includes(tag.id)}
                              onChange={() => handleOptionChange(tag.id)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div className="mb-8 items-center justify-center flex flex-col pt-2">
          <WideButton
            disabled={selectedOptions.length <= 0}
            type="filled"
            onClick={() => onContinue()}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={onSkip}
            variant="link"
            className="body-l-semibold underline text-links mt-6"
          >
            {i18n.get("skipForNow")}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep4;
